import { useQuery } from '@tanstack/react-query';
import { withConflictConfirm } from '../availability/conflicts';
import { useState } from 'react';
import { api, ApiError } from '../api';
import { can, fieldVisible, type Me } from '../auth/me';
import { LINES_RESOURCE, QUOTES_KEY, type Version } from './types';
import { trapTab } from '../ui/focus';
import { DialogLayer } from '../ui/Dialog';

export const QUOTE_BLOCKS_RESOURCE = 'quotes.blocks';
export const EQUIPMENT_BLOCKS_RESOURCE = 'templates.blocks';

type BlockSummary = { id: string; name: string; lineCount?: number };

/** Lower case, no Polish diacritics: "Mikrofony" is found by "mikrofon", "Obsługa" by "obsluga". */
const normalizeSearchText = (text: string) => text.normalize('NFD').replace(/\p{M}+/gu, '').toLowerCase().replace(/ł/g, 'l').replace(/\s+/g, ' ').trim();
type Tab = 'quote' | 'equipment';

/** Who may insert which blocks (the server checks the same). */
export function blockRights(me: Me) {
  const quantity = fieldVisible(me, LINES_RESOURCE, 'quantity');
  return {
    insertQuote: quantity && can(me, QUOTE_BLOCKS_RESOURCE, 'view'),
    insertEquipment: quantity && can(me, EQUIPMENT_BLOCKS_RESOURCE, 'view'),
    saveQuote: quantity && can(me, QUOTE_BLOCKS_RESOURCE, 'create'),
    saveEquipment: quantity && can(me, EQUIPMENT_BLOCKS_RESOURCE, 'create'),
  };
}

const plural = (n: number) => (n === 1 ? 'pozycja' : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 'pozycje' : 'pozycji');

/**
 * "Wstaw blok" (F26, owner 4/33: visible at once next to "+ Sekcja"): two tabs — "Bloki kosztorysu" (saved sections
 * with services and prices) and "Bloki sprzętu" (F13: equipment from the catalog, times a multiplier, with the catalog's
 * prices). No "are you sure": the lines land in the draft and can be removed one by one.
 */
export function InsertBlockDialog({ me, version, onDone, onClose }: { me: Me; version: Version; onDone: (message: string) => void; onClose: () => void }) {
  const rights = blockRights(me);
  const [tab, setTab] = useState<Tab>(rights.insertQuote ? 'quote' : 'equipment');
  const [query, setQuery] = useState('');
  const [chosen, setChosen] = useState<string | null>(null);
  const [multiplier, setMultiplier] = useState('1');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const quoteBlocks = useQuery({ queryKey: [...QUOTES_KEY, 'blocks'], enabled: rights.insertQuote, queryFn: () => api<{ blocks: BlockSummary[] }>('/api/quotes/blocks') });
  const equipmentBlocks = useQuery({ queryKey: ['templates', 'blocks', 'for-quote'], enabled: rights.insertEquipment, queryFn: () => api<{ blocks: BlockSummary[] }>('/api/templates/blocks') });
  const list = (tab === 'quote' ? quoteBlocks : equipmentBlocks).data?.blocks ?? [];
  const wanted = normalizeSearchText(query);
  const shown = wanted === '' ? list : list.filter((b) => normalizeSearchText(b.name).includes(wanted));
  const block = list.find((b) => b.id === chosen) ?? null;
  const loading = (tab === 'quote' ? quoteBlocks : equipmentBlocks).isLoading;

  async function insert() {
    if (!block) return;
    setBusy(true);
    setError('');
    try {
      if (tab === 'quote') {
        // owner 2026-10-23 20:44: a block only adds lines (gear already there stays a line of its own; "Scal powtórzenia" merges)
        // one „Dodaj mimo to” / „Anuluj” for the whole block (owner 2026-10-24)
        const r = await withConflictConfirm((confirmConflicts) => api<{ added: number; skipped: string[] }>(`/api/quotes/versions/${version.id}/insert-quote-block`, { method: 'POST', body: { blockId: block.id, ...(confirmConflicts ? { confirmConflicts } : {}) } }));
        if (!r) return setBusy(false);
        onDone(`Wstawiono blok „${block.name}”: ${r.added} ${plural(r.added)}${r.skipped.length > 0 ? `, pominięto ${r.skipped.length} (usunięte z cennika lub katalogu)` : ''}.`);
      } else {
        const m = Number(multiplier.replace(',', '.'));
        if (!Number.isInteger(m) || m < 1 || m > 1000) {
          setError('Krotność: liczba całkowita od 1 do 1000');
          setBusy(false);
          return;
        }
        const r = await withConflictConfirm((confirmConflicts) => api<{ added: number; skipped: string[] }>(`/api/quotes/versions/${version.id}/insert-block`, { method: 'POST', body: { blockId: block.id, multiplier: m, ...(confirmConflicts ? { confirmConflicts } : {}) } }));
        if (!r) return setBusy(false);
        onDone(`Wstawiono blok „${block.name}”${m > 1 ? ` × ${m}` : ''}: ${r.added} ${plural(r.added)}${r.skipped.length > 0 ? `, pominięto ${r.skipped.length} (usunięte z katalogu)` : ''}.`);
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Nie udało się wstawić bloku');
      setBusy(false);
    }
  }

  const switchTab = (t: Tab) => {
    setTab(t);
    setChosen(null);
    setError('');
  };

  return (
    <div className="dialog-backdrop">
      <div
        className="dialog panel q-insert-block"
        role="dialog"
        aria-modal="true"
        aria-labelledby="insert-block-title"
        data-feature-id="quotes.insert_block"
        onKeyDown={(e) => {
          if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
        }}
      >
          <DialogLayer onClose={onClose} busy={busy} />
        <h2 id="insert-block-title">Wstaw blok</h2>
        <div role="tablist" aria-label="Rodzaj bloku" className="segment">
          {rights.insertQuote && (
            <button role="tab" type="button" aria-selected={tab === 'quote'} onClick={() => switchTab('quote')}>
              Bloki kosztorysu
            </button>
          )}
          {rights.insertEquipment && (
            <button role="tab" type="button" aria-selected={tab === 'equipment'} onClick={() => switchTab('equipment')}>
              Bloki sprzętu
            </button>
          )}
        </div>
        <p className="muted">
          {tab === 'quote'
            ? 'Zapisane sekcje kosztorysów: sprzęt, pakiety, usługi i pozycje własne z ilościami (i cenami, jeśli je zapisano). Usługi trafiają do „Obsługi”.'
            : 'Bloki ze sprzętówek (np. „Mikrofony”): sprzęt z katalogu w sekcji o nazwie bloku, z cenami z katalogu. Zestawy są rozwijane.'}
        </p>
        <label className="field">
          <span>Szukaj bloku</span>
          <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="np. Realizacja, Mikrofony" autoComplete="off" />
        </label>
        {loading && (
          <p className="muted" role="status">
            Wczytywanie…
          </p>
        )}
        {!loading && list.length === 0 && (
          <p className="muted">{tab === 'quote' ? 'Nie ma jeszcze bloków kosztorysu. Zapisz sekcję kosztorysu jako blok w menu „…”.' : 'Nie ma jeszcze bloków sprzętu.'}</p>
        )}
        {shown.length > 0 && (
          <ul className="q-block-list" role="listbox" aria-label="Bloki">
            {shown.map((b) => (
              <li key={b.id} role="option" aria-selected={chosen === b.id}>
                <button type="button" className={`btn small${chosen === b.id ? ' primary' : ' ghost'}`} onClick={() => setChosen(b.id)} onDoubleClick={() => void insert()}>
                  {b.name}
                  {b.lineCount !== undefined && (
                    <span className="muted">
                      {' '}
                      · {b.lineCount} {plural(b.lineCount)}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
        {tab === 'equipment' && (
          <label className="field q-block-multiplier">
            <span>Ile razy</span>
            <input inputMode="numeric" value={multiplier} onChange={(e) => setMultiplier(e.target.value)} />
          </label>
        )}
        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="button" disabled={!block || busy} onClick={() => void insert()}>
            {busy ? 'Wstawianie…' : block ? `Wstaw „${block.name}”` : 'Wstaw'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}
